import React, { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import StudentRisk, { isCycleRisky } from '../../../StudentRisk/StudentRisk';
import moment from '../../../../moment';
import Table from './Table';
import { DEFAULT_RISKY_EXPIRATION_PERIOD_IN_DAYS } from '../../../../utils/date';
import { getCurrentCode95Cycle } from '../../../../utils/code95';
import { compareDates } from '../../../../utils/sort';
import { StudentDataEntry } from '../DashboardTable';

const columnHelper = createColumnHelper<Code95CycleDataEntry>();
export type Cycle = {
	practiceHours: number;
	theoryHours: number;
	endDate: string;
	id: string;
};
export type Code95CycleDataEntry = StudentDataEntry & {
	displayName: string;
	currentCycle: Cycle;
	endDate: string;
	code95Student: boolean;
	code95Cycles: Cycle[];
};
const getDateFormat = (date: string) => moment(date).format('L');

const Code95Cycle = ({ students }: { students: Code95CycleDataEntry[] }) => {
	const { t } = useTranslation();

	const columns = useMemo(
		() => [
			columnHelper.accessor('displayName', {
				header: t('dashboard.name'),
			}),
			columnHelper.accessor('currentCycle.id', {
				header: t('dashboard.code_95_cycle'),
				cell: (info) =>
					info.cell.getValue() ? (
						<StudentRisk
							amount={
								info.row.original.currentCycle.practiceHours +
								info.row.original.currentCycle.theoryHours
							}
							endDate={moment(info.row.original.currentCycle.endDate)}
						/>
					) : (
						t('dashboard.no_current_cycle')
					),
				enableSorting: false,
			}),
			columnHelper.accessor('endDate', {
				header: t('dashboard.expiry_date'),
				cell: (info) => (info.cell.getValue() ? getDateFormat(info.cell.getValue()) : ''),
				sortingFn: (rowA, rowB) =>
					compareDates(moment(rowA.original.endDate), moment(rowB.original.endDate)),
			}),
		],
		[t],
	);

	const filteredStudents = React.useMemo(() => {
		if (students === null) {
			return [];
		}
		return students
			.filter((student) => {
				if (!student.code95Student) {
					return false;
				}
				const currentCycle = getCurrentCode95Cycle(student.code95Cycles);
				if (!currentCycle) {
					return true;
				}
				const { practiceHours, theoryHours, endDate } = currentCycle;
				const date = moment(endDate);
				const duration = moment.duration(moment().diff(date));
				return (
					isCycleRisky(practiceHours + theoryHours, date) &&
					duration.as('days') > -DEFAULT_RISKY_EXPIRATION_PERIOD_IN_DAYS
				);
			})
			.map((student) => {
				const currentCycle = getCurrentCode95Cycle(student.code95Cycles);
				return {
					student: student,
					displayName: student.displayName,
					currentCycle,
					endDate: currentCycle?.endDate,
					id: student.id,
					code95Student: student.code95Student,
					code95Cycles: student.code95Cycles,
				};
			});
	}, [students]);
	return <Table data={filteredStudents} columns={columns} />;
};

export default Code95Cycle;
